/**
 * PractitionerAccessScreen — Screen root for Client Consent Settings.
 *
 * Client (patient) owns this screen. Grants/revokes what practitioners can see.
 * Uses GlassBackground + ScrollView with gap-4 layout.
 *
 * States: loading skeleton | error retry | empty | list of cards
 */

import React, { useCallback, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { GlassBackground } from '../../glass';
import { GlassCard } from '../../glass';
import { ConsentExplainerCard } from './ConsentExplainerCard';
import { PractitionerConsentCard } from './PractitionerConsentCard';
import { WhatCanTheySeeAccordion } from './WhatCanTheySeeAccordion';
import { usePractitionerConsent } from '@/hooks/usePractitionerConsent';
import type { ConsentField, PractitionerConsentRecord } from '@/hooks/usePractitionerConsent';
import { trackEvent } from '@/lib/analytics/events';

interface PractitionerAccessScreenProps {
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Wrapper to provide stable per-record callbacks without inline arrow functions
// ---------------------------------------------------------------------------

interface ConsentCardWrapperProps {
  record: PractitionerConsentRecord;
  onConsentChange: (practitionerId: string, field: ConsentField, value: boolean) => Promise<void>;
  onRevoke: (record: PractitionerConsentRecord) => Promise<void>;
}

const PractitionerConsentCardWrapper: React.FC<ConsentCardWrapperProps> = React.memo(
  function PractitionerConsentCardWrapper({ record, onConsentChange, onRevoke }) {
    const handleConsentChange = useCallback(
      (field: ConsentField, value: boolean) => onConsentChange(record.practitionerId, field, value),
      [record.practitionerId, onConsentChange]
    );

    const handleRevoke = useCallback(
      () => onRevoke(record),
      [record, onRevoke]
    );

    return (
      <PractitionerConsentCard
        record={record}
        onConsentChange={handleConsentChange}
        onRevoke={handleRevoke}
      />
    );
  }
);

export const PractitionerAccessScreen: React.FC<PractitionerAccessScreenProps> = ({
  onBack,
}) => {
  const { t } = useTranslation('practitioner_consent');
  const { records, isLoading, error, updateConsent, revokePractitioner, refetch } =
    usePractitionerConsent();

  // Track screen view
  useEffect(() => {
    trackEvent('Screen_View_PractitionerConsent', {});
  }, []);

  // Derive accordion data from first active practitioner
  const firstActive = records.find((r) => r.status === 'active');

  const handleConsentChange = useCallback(
    async (practitionerId: string, field: ConsentField, value: boolean): Promise<void> => {
      await updateConsent(practitionerId, field, value);
      trackEvent('Consent_Toggle_Changed', {
        field,
        value,
        practitioner_id: practitionerId,
      });
    },
    [updateConsent]
  );

  const handleRevoke = useCallback(
    async (record: PractitionerConsentRecord) => {
      await revokePractitioner(record.practitionerId);
      trackEvent('Consent_Revoked', { practitioner_id: record.practitionerId });
    },
    [revokePractitioner]
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#A78BFA" />
        </View>
      );
    }

    if (error) {
      return (
        <GlassCard withChromeBorder={false} style={styles.errorCard}>
          <Text style={styles.errorText}>{t('error.load_failed')}</Text>
          <Pressable
            onPress={refetch}
            style={({ pressed }) => [styles.retryButton, pressed && styles.retryButtonPressed]}
            accessibilityRole="button"
          >
            <Text style={styles.retryLabel}>{t('error.retry')}</Text>
          </Pressable>
        </GlassCard>
      );
    }

    if (records.length === 0) {
      return (
        <GlassCard withChromeBorder={false} style={styles.emptyCard}>
          <Text style={styles.emptyTitle}>{t('empty.title')}</Text>
          <Text style={styles.emptyDescription}>{t('empty.description')}</Text>
        </GlassCard>
      );
    }

    return (
      <>
        {records.map((record) => (
          <PractitionerConsentCardWrapper
            key={record.id}
            record={record}
            onConsentChange={handleConsentChange}
            onRevoke={handleRevoke}
          />
        ))}

        <WhatCanTheySeeAccordion
          historyOn={firstActive?.consent.canViewHistory ?? false}
          messagesOn={firstActive?.consent.canViewOriginalMessage ?? false}
          crisisOn={firstActive?.consent.allowCrisisNotifications ?? false}
        />
      </>
    );
  };

  return (
    <GlassBackground>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <ConsentExplainerCard />
        {renderContent()}
      </ScrollView>
    </GlassBackground>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 32,
    gap: 16,
  },
  centerContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  errorCard: {
    alignItems: 'center',
    gap: 12,
  },
  errorText: {
    fontSize: 14,
    color: '#71717A',
    textAlign: 'center',
  },
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(167, 139, 250, 0.12)',
    borderRadius: 8,
  },
  retryButtonPressed: {
    opacity: 0.7,
  },
  retryLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#A78BFA',
  },
  emptyCard: {
    gap: 8,
    alignItems: 'center',
    paddingVertical: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#18181B',
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 14,
    color: '#71717A',
    textAlign: 'center',
    lineHeight: 20,
  },
});
